import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useState } from "react";
import { FormProvider, Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useGoogleServices } from "@ryde/redux";
import {
  AreaApi,
  cleanSecondaryText,
  errors,
  IPlacePrediction,
  Log,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { Address } from "../../../../../common/map/address";
import { StaticMap } from "../../../../../common/map/static-map";
import { MuiButton } from "../../../../../controls/forms/buttons/button";
import { DropdownBoundTemplate } from "../../../dropdown-bound.template";
import {
  IQuickAddStationFormModel,
  IQuickStationForm,
  QuickStationFormFields,
} from "./quick-add-station-form.models";
import { QuickAddStationFormStyled } from "./quick-add-station-form.styled";
import { useQuickAddValidationSchema } from "./use-quick-add-station.form.validation";

export const QuickAddStationForm: FC<IQuickStationForm> = ({
  closeQuickAdd,
  onSave,
}) => {
  const { t } = useTranslation();
  const { quickAddStationFormValidationRules } = useQuickAddValidationSchema();

  const methods = useForm<IQuickAddStationFormModel>({
    resolver: yupResolver(quickAddStationFormValidationRules) as Resolver<
      IQuickAddStationFormModel,
      unknown
    >,
    defaultValues: {
      [QuickStationFormFields.address]: undefined,
      [QuickStationFormFields.areaId]: undefined,
      [QuickStationFormFields.coordinate]: undefined,
    },
  });

  const {
    getValues,
    setValue,
    watch,
    formState: { isValid, dirtyFields },
  } = methods;

  const { getCoordinateByPlaceId } = useGoogleServices();
  const [isAreaSuccess, setIsAreaSuccess] = useState<boolean>(false);
  const watchedAddress = watch(QuickStationFormFields.address);
  const watchedCoordinate = watch(QuickStationFormFields.coordinate);

  const handleAddressChange = useEvent(
    async (newAddress: IPlacePrediction | null): Promise<void> => {
      if (!newAddress) {
        Log.error("No address");
        return;
      }

      setValue(QuickStationFormFields.address, newAddress, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (!newAddress?.placeId) {
        Log.error("No place_id");
        return;
      }

      try {
        const coordinateResult = await getCoordinateByPlaceId(
          newAddress.placeId,
        );

        if (!coordinateResult.success) {
          warnAndToastSimpleResult(coordinateResult, t);
          return;
        }

        if (!coordinateResult.data) {
          Log.error("No coordinates");
          return;
        }

        setValue(QuickStationFormFields.coordinate, coordinateResult.data, {
          shouldValidate: true,
        });

        if (!coordinateResult.data?.lat || !coordinateResult.data?.lng) {
          Log.error("Coordinate lat/lng is missing");
          return;
        }

        const areas = await AreaApi.findByCoordinates({
          lat: coordinateResult.data.lat,
          lng: coordinateResult.data.lng,
        });

        if (!areas?.length) {
          setIsAreaSuccess(false);
          Log.infoAndToast(
            t("ride.noAreasFondByCoordinatesChooseFromDropdown"),
          );
        } else {
          setIsAreaSuccess(true);
          setValue(QuickStationFormFields.areaId, areas[0].id, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      } catch (err) {
        setIsAreaSuccess(false);
        Log.warnAndToast(t(errors.common.technicalProblemOccurred), err);
      }
    },
  );

  const handleSave = (): void => {
    const formData = getValues();

    if (!formData.address?.text?.text) {
      Log.warnAndToast(t("general.PLEASE_FILL_REQUIRED_FIELDS"));
      return;
    }

    onSave({
      address: cleanSecondaryText(formData.address.text?.text),
      areaId: formData.areaId,
      coordinate: formData.coordinate,
    });
  };

  useEffect(() => {
    return () => {
      closeQuickAdd();
    };
  }, [closeQuickAdd]);

  return (
    <QuickAddStationFormStyled>
      <FormProvider {...methods}>
        <div className="quick-add-station-form__input-wrapper">
          <Address
            value={(watchedAddress as IPlacePrediction) ?? null}
            onChange={handleAddressChange}
            label={t("station.formLabel.locationInfo")}
            error={dirtyFields.address && !watchedAddress?.text?.text}
            size="small"
            fullWidth
          />

          <DropdownBoundTemplate
            name={QuickStationFormFields.areaId}
            size="small"
            labelTranslated={t("station.formLabel.areaId")}
            formAttributes={{
              dropItemsSource: "area",
              dropItemsDisableAdd: true,
              disabled: !watchedAddress?.text?.text,
              hideDropdownEditButton: true,
            }}
          />
        </div>

        <div className="quick-add-station-form__map-wrapper">
          <StaticMap
            coordinate={watchedCoordinate}
            isZoomAllowed={true}
            isDraggingAllowed={true}
          />
        </div>

        <div className="quick-add-station-form__controls">
          <MuiButton onClick={closeQuickAdd} muiVariant="text">
            {t("general.CANCEL")}
          </MuiButton>
          <MuiButton onClick={handleSave} muiVariant="text" disabled={!isValid}>
            {t("general.SAVE")}
          </MuiButton>
        </div>
      </FormProvider>
    </QuickAddStationFormStyled>
  );
};
